/**
 * Importar sobras a partir da TabelaSobras (.xls/.xlsx do gerenciador).
 *
 * Mesmo fluxo da tela de Importações (simular → revisar → aplicar), aqui
 * dentro do Aproveitamento de Sobras. Linha com material que não existe no
 * Cadastro de Suprimentos aparece na simulação numa área de RELACIONAR:
 * o usuário escolhe a chapa cadastrada (busca tolerante) ou clica "+" e
 * cadastra o material na hora, já com nome e espessura preenchidos. O
 * mapeamento vai junto na importação — nada entra pela metade.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FiAlertTriangle, FiCheckCircle, FiDownload, FiPlus, FiUploadCloud } from "react-icons/fi";

import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { criarProduto, listarProdutos, listarTiposSuprimento } from "../../../services/estoque";
import { baixarModelo, executarImportacao } from "../../../services/importacoes";
import { unidadeDefault } from "../../../services/unidades";
import { nomeMaterial } from "../../../utils/nomeMaterial";
import FormularioProduto from "../Produtos/FormularioProduto";
import ComboboxBusca from "../Solicitacoes/ComboboxBusca";
import { ErrorMsg, IconButton, InlineHint, PrimaryButton, SecondaryButton } from "../styles";

const TIPO = "sobras";

const FileDrop = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  border: 2px dashed ${({ theme }) => theme.border};
  border-radius: 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.textSecondary};
  background: ${({ theme }) => theme.surfaceAlt};

  &:hover {
    border-color: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.accent};
  }
  input[type="file"] { display: none; }
  strong { font-weight: 600; color: ${({ theme }) => theme.textPrimary}; }
`;

const Grade = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 8px;
`;

const Numero = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  padding: 10px 12px;
  small { display: block; font-size: 11px; color: ${({ theme }) => theme.textSecondary}; text-transform: uppercase; letter-spacing: 0.04em; }
  strong { font-size: 1.2rem; }
`;

const Lista = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 160px;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  font-size: 12.5px;
  li { padding: 6px 10px; border-bottom: 1px solid ${({ theme }) => theme.border}; }
  li:last-child { border-bottom: 0; }
  li.erro { color: var(--danger-text); }
`;

const Relacionar = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  h4 { margin: 0; font-size: 0.85rem; font-weight: 600; }
  .linha { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) auto; gap: 8px; align-items: center; }
  .material { min-width: 0; font-size: 13px; }
  .material strong { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .material small { color: ${({ theme }) => theme.textSecondary}; }
  .ok { color: var(--success-text); font-size: 12px; }
`;

/** "Maxi Branco 18 mm" -> "18"; nada -> "". */
function espessuraDoNome(material) {
  const m = /(\d+(?:[.,]\d+)?)\s*mm/i.exec(material || "");
  return m ? String(Math.round(Number(m[1].replace(",", ".")))) : "";
}

export default function ImportarSobrasPanel({ open, onClose, onImportou }) {
  const { request } = useAuth();
  const toast = useToast();
  const [arquivo, setArquivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [aplicado, setAplicado] = useState(null);
  const [executando, setExecutando] = useState(false);
  const [erro, setErro] = useState(null);
  // material da planilha -> produto (objeto {id, label, ...}) escolhido na tela
  const [mapeamento, setMapeamento] = useState({});
  const [chapas, setChapas] = useState([]);
  const [tiposCatalogo, setTiposCatalogo] = useState([]);
  const [unidade, setUnidade] = useState(null);
  // cadastro na hora: estado do FormularioProduto + material que ele atende
  const [novoProduto, setNovoProduto] = useState(null);
  const [materialDoNovo, setMaterialDoNovo] = useState(null);

  const carregarChapas = useCallback(async () => {
    try {
      const p = await listarProdutos(request, { pageSize: 2000, categoria: "CHAPA", ativo: true });
      setChapas(Array.isArray(p) ? p : p?.results || []);
    } catch {
      setChapas([]);
    }
  }, [request]);

  useEffect(() => {
    if (!open) return;
    carregarChapas();
    listarTiposSuprimento(request).then((r) => setTiposCatalogo(r?.tipos || [])).catch(() => setTiposCatalogo([]));
    unidadeDefault(request).then(setUnidade).catch(() => setUnidade(null));
  }, [open, request, carregarChapas]);

  const opcoesChapas = useMemo(
    () => chapas.map((p) => ({ id: p.id, label: nomeMaterial(p), sublabel: p.codigo, codigo: p.codigo })),
    [chapas],
  );
  const tipoAtual = useMemo(
    () => tiposCatalogo.find((t) => t.codigo === novoProduto?.tipo_suprimento),
    [tiposCatalogo, novoProduto?.tipo_suprimento],
  );

  const limpar = () => { setArquivo(null); setPreview(null); setAplicado(null); setErro(null); setMapeamento({}); };
  const fechar = () => { limpar(); onClose(); };

  const mapeamentoJson = () => {
    const ids = Object.fromEntries(Object.entries(mapeamento).filter(([, p]) => p?.id).map(([m, p]) => [m, p.id]));
    return Object.keys(ids).length ? JSON.stringify(ids) : "";
  };

  const rodar = async (dryRun) => {
    if (!arquivo) return;
    setExecutando(true);
    setErro(null);
    try {
      const r = await executarImportacao(request, TIPO, {
        arquivo, dryRun, incluirDetalhes: true, campos: { mapeamento: mapeamentoJson() },
      });
      if (dryRun) setPreview(r);
      else {
        setAplicado(r);
        setPreview(null);
        toast.success(`${r.criados} sobra(s) importada(s).`);
        await onImportou?.();
      }
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.payload?.arquivo || err?.message || "Falha na importação.");
    } finally {
      setExecutando(false);
    }
  };

  const semPar = preview?.extras?.materiais_sem_par || [];
  const pendentes = semPar.filter((m) => !mapeamento[m.material]?.id).length;
  const relacionados = semPar.length - pendentes;

  const abrirCadastro = (m) => {
    const esp = m.espessura_mm ? String(Math.round(Number(m.espessura_mm))) : espessuraDoNome(m.material);
    setMaterialDoNovo(m.material);
    setNovoProduto({
      codigo: "", nome: m.material, descricao: "", unidade: "CH", categoria: "CHAPA",
      custo_medio: null, estoque_minimo: "0", ativo: true, m2_por_unidade: "",
      largura_mm: "", comprimento_mm: "", espessura_mm: esp, tem_veio: null,
      tipo_suprimento: "MDF_BP", atributos: {}, fabricante: "", referencia_fabricante: "", ncm: "",
    });
  };

  const salvarNovoProduto = async (e) => {
    e.preventDefault();
    const p = novoProduto;
    try {
      const criado = await criarProduto(request, {
        codigo: p.codigo, nome: p.nome, descricao: p.descricao || "", unidade: p.unidade, categoria: p.categoria,
        custo_medio: p.custo_medio === null || p.custo_medio === undefined ? "0" : String(p.custo_medio),
        estoque_minimo: p.estoque_minimo || "0", tipo_suprimento: p.tipo_suprimento || "",
        fabricante: p.fabricante || "", referencia_fabricante: p.referencia_fabricante || "", ncm: p.ncm || "",
        atributos: p.atributos || {}, ativo: p.ativo ?? true, m2_por_unidade: p.m2_por_unidade || null,
        business_unit: unidade?.id,
        largura_mm: p.largura_mm || null, comprimento_mm: p.comprimento_mm || null,
        espessura_mm: p.espessura_mm || null, tem_veio: p.tem_veio ?? null,
      });
      toast.success(`${criado.codigo} cadastrado e relacionado.`);
      setMapeamento((prev) => ({ ...prev, [materialDoNovo]: { id: criado.id, label: nomeMaterial(criado), sublabel: criado.codigo, codigo: criado.codigo } }));
      setNovoProduto(null);
      setMaterialDoNovo(null);
      await carregarChapas();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Erro ao cadastrar o material.");
    }
  };

  const linhasProblema = (r) => (r?.detalhes || []).filter((l) => l.acao === "erro" || l.acao === "descartado");

  const Resultado = ({ r }) => (
    <>
      <Grade>
        <Numero><small>Linhas</small><strong>{r.linhas_totais}</strong></Numero>
        <Numero><small>{r.modo === "dry_run" ? "Entrariam" : "Criadas"}</small><strong>{r.criados}</strong></Numero>
        <Numero><small>Já existiam</small><strong>{r.ignorados}</strong></Numero>
        <Numero><small>Com problema</small><strong style={{ color: r.erros + r.descartados ? "var(--danger-text)" : undefined }}>{r.erros + r.descartados}</strong></Numero>
      </Grade>
      {linhasProblema(r).length > 0 && (r.modo !== "dry_run" || semPar.length === 0) && (
        <Lista aria-label="Linhas com problema">
          {linhasProblema(r).map((l) => (
            <li key={`${l.linha}-${l.motivo}`} className="erro">Linha {l.linha}: {l.motivo}</li>
          ))}
        </Lista>
      )}
    </>
  );

  return (
    <>
      <SlideOverPanel open={open} onClose={fechar} title="Importar sobras" subtitle="TabelaSobras do gerenciador (.xls ou .xlsx)" width="min(94vw, 640px)">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <SecondaryButton type="button" onClick={() => baixarModelo(TIPO).catch((e) => toast.error(e.message))}>
              <FiDownload size={13} /> Baixar modelo
            </SecondaryButton>
          </div>

          {!aplicado && (
            <FileDrop>
              <FiUploadCloud size={26} aria-hidden="true" />
              <strong>{arquivo ? arquivo.name : "Clique para escolher a planilha"}</strong>
              <span>Aba "Sobras": Material, Local, Comprimento, Largura, Espessura, Quantidade…</span>
              <input
                type="file"
                accept=".xls,.xlsx,.xlsm"
                disabled={executando}
                onChange={(e) => { setArquivo(e.target.files?.[0] || null); setPreview(null); setErro(null); setMapeamento({}); }}
              />
            </FileDrop>
          )}

          {erro && <ErrorMsg><FiAlertTriangle size={14} /> {erro}</ErrorMsg>}

          {arquivo && !preview && !aplicado && (
            <div style={{ display: "flex", gap: 8 }}>
              <PrimaryButton type="button" onClick={() => rodar(true)} disabled={executando}>
                {executando ? "Simulando…" : "Simular importação"}
              </PrimaryButton>
              <SecondaryButton type="button" onClick={limpar} disabled={executando}>Trocar arquivo</SecondaryButton>
            </div>
          )}

          {preview && (
            <>
              <Resultado r={preview} />

              {semPar.length > 0 && (
                <Relacionar>
                  <h4>
                    Relacionar materiais ({relacionados}/{semPar.length})
                  </h4>
                  <InlineHint>
                    Estes nomes da planilha não casaram com nenhuma chapa do Cadastro de Suprimentos.
                    Escolha a chapa correspondente ou clique em + para cadastrar na hora.
                  </InlineHint>
                  {semPar.map((m) => (
                    <div className="linha" key={m.material}>
                      <div className="material" title={m.material}>
                        <strong>{m.material}</strong>
                        <small>{m.linhas} linha(s) da planilha{m.espessura_mm ? ` · ${m.espessura_mm} mm` : ""}</small>
                      </div>
                      <ComboboxBusca
                        opcoes={opcoesChapas}
                        valor={mapeamento[m.material] || null}
                        onSelecionar={(op) => setMapeamento((prev) => ({ ...prev, [m.material]: op }))}
                        placeholder="Buscar chapa cadastrada…"
                        carregando={chapas.length === 0}
                      />
                      <IconButton
                        type="button"
                        title={`Cadastrar "${m.material}" agora`}
                        aria-label={`Cadastrar ${m.material}`}
                        onClick={() => abrirCadastro(m)}
                      >
                        <FiPlus size={13} />
                      </IconButton>
                    </div>
                  ))}
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <SecondaryButton type="button" onClick={() => rodar(true)} disabled={executando || relacionados === 0}>
                      {executando ? "Simulando…" : "Simular de novo com os relacionados"}
                    </SecondaryButton>
                    {pendentes > 0 ? (
                      <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{pendentes} sem par ficam de fora se importar agora.</span>
                    ) : (
                      <span className="ok"><FiCheckCircle size={12} /> Tudo relacionado — simule de novo para conferir.</span>
                    )}
                  </div>
                </Relacionar>
              )}

              <InlineHint>
                Simulação: nada foi gravado. Linhas com problema ficam de fora; as demais entram ao confirmar.
                Reimportar a mesma planilha não duplica.
              </InlineHint>
              <div style={{ display: "flex", gap: 8 }}>
                <PrimaryButton type="button" onClick={() => rodar(false)} disabled={executando || preview.criados === 0}>
                  {executando ? "Importando…" : `Confirmar e importar ${preview.criados}`}
                </PrimaryButton>
                <SecondaryButton type="button" onClick={() => setPreview(null)} disabled={executando}>Voltar</SecondaryButton>
              </div>
            </>
          )}

          {aplicado && (
            <>
              <Resultado r={aplicado} />
              <InlineHint><FiCheckCircle size={13} /> Importação concluída e registrada no histórico de importações.</InlineHint>
              <div style={{ display: "flex", gap: 8 }}>
                <SecondaryButton type="button" onClick={limpar}>Importar outra</SecondaryButton>
                <PrimaryButton type="button" onClick={fechar}>Fechar</PrimaryButton>
              </div>
            </>
          )}
        </div>
      </SlideOverPanel>

      {/* Cadastro na hora: o mesmo formulário do Cadastro de Suprimentos,
          já com nome, tipo (MDF) e espessura vindos da planilha. */}
      <FormularioProduto
        editando={novoProduto}
        setEditando={setNovoProduto}
        tiposCatalogo={tiposCatalogo}
        tipoAtual={tipoAtual}
        onSalvar={salvarNovoProduto}
        onCancelar={() => { setNovoProduto(null); setMaterialDoNovo(null); }}
      />
    </>
  );
}
